import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import * as Sentry from '@sentry/browser';
import { parse } from 'graphql';
import { GraphQLClient, gql } from 'graphql-request';

export type ShopifyStorefront = {
   storeCode: string;
   storeDomain: string;
   storefrontAccessToken: string;
   apiVersion: string;
   multipassToken: string;
   countryCode: string;
   language: string;
   nextLocale: string;
};

type ShopifyApiClientProps = {
   storeDomain: string;
   storefrontAccessToken: string;
   version: string;
};

export function shopifyApiClient({
   storeDomain,
   storefrontAccessToken,
   version,
}: ShopifyApiClientProps) {
   const url = `https://${storeDomain}.myshopify.com/api/${version}/graphql.json`;
   const headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
   };
   const graphQLClient = new GraphQLClient(url, { headers });
   return graphQLClient;
}

type ShopifyCustAccessTokenData = {
   customerAccessTokenCreateWithMultipass: {
      customerAccessToken: {
         accessToken: string;
         expiresAt: string;
      };
      customerUserErrors: {
         code: string;
         field: string;
         message: string;
      }[];
   };
};

export async function getCustomerAccessToken(graphQLClient: GraphQLClient, multipassToken: string) {
   const mutation: TypedDocumentNode<ShopifyCustAccessTokenData, { multipassToken: string }> =
      parse(gql`
     mutation ($multipassToken: String!) {
        customerAccessTokenCreateWithMultipass(multipassToken: $multipassToken) {
           customerAccessToken {
              accessToken
              expiresAt
           }
           customerUserErrors {
              code
              field
              message
           }
        }
     }
   `);
   const variables = { multipassToken: multipassToken };
   const response = await graphQLClient.request(mutation, variables);
   const data = response.customerAccessTokenCreateWithMultipass;
   if (data.customerUserErrors.length) {
      const msg = 'Order history customer access token user errors';
      console.error(msg, data.customerUserErrors);
      Sentry.captureMessage(msg, {
         extra: {
            mutation,
            variables,
            'User Errors': data.customerUserErrors,
         },
      });
      return null;
   }
   return data.customerAccessToken.accessToken;
}
